'use client'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper/modules'
import { useSelector } from '@/lib/store'
import { useEffect, useRef, useState } from 'react'
import { Swiper as SwiperType } from 'swiper'
import CarouselNavigation from '@/components/common/CarouselNavigation'
import {
  getCarePackagesUrl,
  getCarePackageCategoryUrl,
} from '@/utils/commonFunc'
import { DEFAULT_CITY_SLUG } from '@/utils/constants'
import Image from 'next/image'
import Link from 'next/link'
import { ArrowRight } from 'lucide-react'

interface CompleteCarePackageSectionProps {
  ssrData?: any[] | null
}

const CompleteCarePackageSection = ({
  ssrData = null,
}: CompleteCarePackageSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false)
  useEffect(() => {
    setIsHydrated(true)
  }, [])

  const cityState = useSelector((state) => state.city) || {}

  // Shape-checked, not just defaulted: a malformed payload could make this a
  // string or an object, and .filter/.length below would throw.
  const carePackages = Array.isArray(ssrData) ? ssrData : []

  const swiperRef = useRef<SwiperType | null>(null)

  const goToNextSlide = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext()
    }
  }

  const goToPrevSlide = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev()
    }
  }

  // The persisted city only reaches Redux after mount, so this stays on its SSR value
  // until hydration completes.
  const citySlug =
    (isHydrated ? cityState?.currentCityObj?.slugName : DEFAULT_CITY_SLUG) ||
    DEFAULT_CITY_SLUG

  const carePackagesUrl = getCarePackagesUrl(citySlug)

  return (
    <section
      id="agilus-care-package"
      className="w-full py-6 md:py-10 complete_care_package bg-white"
    >
      <div className="container max-w-full">
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <div>
            <h3 className="text-heading-md md:text-heading-lg text-gray-900 mb-1 sm:mb-2">
              DDRC Agilus Complete Care Packages
            </h3>
          </div>
          <div className="items-center gap-4 hidden md:flex">
            <Link
              href={carePackagesUrl}
              className="flex items-center gap-2 font-semibold text-label-md tracking-wide bg-white px-4 py-2 rounded-md text-primary-600 border border-primary-100"
            >
              View all
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
        {!carePackages.length ? null : (
          <div className="relative">
            {carePackages.length > 5 && (
              <CarouselNavigation
                goToPrevSlide={goToPrevSlide}
                goToNextSlide={goToNextSlide}
                className="!top-[70px] sm:!top-[90px] md:!top-[100px]"
              />
            )}
            <Swiper
              onSwiper={(swiper) => (swiperRef.current = swiper)}
              navigation
              slidesPerView="auto"
              spaceBetween={16}
              className="w_fit_slider navigation_hidden !overflow-visible"
              modules={[Navigation]}
            >
              {carePackages
                .filter((item: any) => item?.packageId)
                .map((item: any) => {
                  // `?.` guards null but not a non-string: .toLowerCase() on a
                  // numeric name throws. Resolved once and reused below.
                  const name = typeof item?.name === 'string' ? item.name : ''
                  const imageUrl =
                    typeof item?.webImageUrl === 'string'
                      ? item.webImageUrl
                      : ''

                  return (
                    <SwiperSlide key={item.packageId} className="!w-auto">
                      <Link
                        href={getCarePackageCategoryUrl(citySlug, name)}
                        className="group flex flex-col items-center gap-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 rounded-lg transition-all duration-200"
                      >
                        <div className="relative w-[144px] h-[144px] sm:w-[170px] sm:h-[170px] md:w-[204px] md:h-[204px] rounded-lg overflow-hidden bg-gray-50 group-hover:scale-[1.02] transition-all duration-200">
                          {imageUrl ? (
                            <Image
                              src={imageUrl}
                              alt={name || 'Care package'}
                              fill
                              sizes="(max-width: 640px) 144px, (max-width: 768px) 170px, 204px"
                              className="object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-100" />
                          )}
                          {name.toLowerCase().includes('active care') && (
                            <div className="absolute top-2 left-0 z-10">
                              <span className="bg-gradient-to-r from-primary-600 to-primary-500 text-white text-label-xs font-semibold py-1 px-2.5 rounded-r-full">
                                Most Popular
                              </span>
                            </div>
                          )}
                        </div>
                        <p className="capitalize text-center text-label-sm md:text-label-lg leading-tight max-w-[144px] sm:max-w-[170px] md:max-w-[204px] line-clamp-2 text-gray-900 group-hover:text-gray-950 transition-colors duration-200">
                          {name.toLowerCase()}
                        </p>
                      </Link>
                    </SwiperSlide>
                  )
                })}
            </Swiper>
            <Link
              href={carePackagesUrl}
              className="mt-6 w-full lg:hidden flex items-center justify-center gap-2 font-semibold text-label-md tracking-wide uppercase bg-white py-3 rounded-md border border-primary-100 text-primary-600 hover:text-primary-700 transition-colors duration-200"
            >
              View all
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  )
}

export default CompleteCarePackageSection
